// Core packages
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
  AfterViewInit, Component, OnDestroy, OnInit
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import {
  MatDialog
} from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import {
  finalize,
  take
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { DocumentSettingDetailDialogComponent } from "src/app/modules/settings/components/document-setting-detail-dialog/document-setting-detail-dialog.component";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-document-detail",
  templateUrl: "./document-detail.page.html",
  styleUrls: ["./document-detail.page.scss"],
})
export class DocumentDetailComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Formly config
  form: FormGroup = new FormGroup({});
  formSubElement: FormGroup = new FormGroup({});
  model: any = {};
  modelSubElement: any = {};
  fields: FormlyFieldConfig[] = [];
  fieldsSubElement: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {
    formState: {
      disabled: false,
    },
  };
  optionsSubElement: FormlyFormOptions = {
    formState: {
      disabled: false,
    },
  };
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Formly config

  // BEGIN - Move content form (cross-area move of a 2nd level content/section)
  moveContentForm: FormGroup = new FormGroup({
    contentId: new FormControl("", Validators.required),
    destAreaId: new FormControl("", Validators.required),
  });
  // END - Move content form

  cardIsLoading = true;
  documentTemplateId = "";
  documentTemplate: any[] = [];
  subDocumentTemplateId = "";
  subDocumentTemplate: any[] = [];

  formFirstLevelIsVisible = false;
  formNewIsVisible = false;
  formNewSubElementIsVisible = false;
  
  currentItemTitle = "";
  currentItemId = "";

  condoStructureTemplate: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private settingsService: SettingsService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,
    private cookieService: CookieService,
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("companies.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    this.onGetDocument();
  }

  /**
   * Get document template
   *
   * @since 1.0.0
   */
  onGetDocument(): void {
    this.subscriptions.push(
      this.settingsService.getDocument().subscribe(
        (res) => {
          if (res.status) {
            if (
              res.data &&
              Array.isArray(res.data) &&
              res.data.length > 0 &&
              res.data[0].area &&
              Array.isArray(res.data[0].area)
            ) {
              this.documentTemplateId = res.data[0]._id;
              this.documentTemplate = res.data[0].area;
              this.onGetCondoStructure();
            }
          }
          this.cardIsLoading = false;

          if (this.currentItemId !== "") {
            this.onTitleClicked(this.currentItemId);
          }
        },
        (err) => {
          this.helperService.handleError(err);
          this.cardIsLoading = false;
        }
      )
    );
  }
  
  /**
   * Get structure template
   *
   * @since 1.0.0
   */
   onGetCondoStructure(): void {
    this.subscriptions.push(
      this.settingsService.getStructure().subscribe(
        (res) => {
          if (res.status) {
            if (
              res.condoStructureTemplate &&
              Array.isArray(res.condoStructureTemplate) &&
              res.condoStructureTemplate.length > 0 &&
              res.condoStructureTemplate[0].area &&
              Array.isArray(res.condoStructureTemplate[0].area)
            ) {
              if (
                res.condoStructureTemplate[0].area &&
                Array.isArray(res.condoStructureTemplate[0].area)
              ) {
                for (const structures of res.condoStructureTemplate[0].area) {
                  if (
                    structures && 
                    structures.content &&
                    Array.isArray(structures.content)
                  ) {
                    for (const structure of structures.content) {
                      if (structure && structure.key) {
                        this.condoStructureTemplate.push({
                          key: structure.key,
                          translation: structure.translation,
                          used: false,
                        });
                      }
                    }
                  }
                }
              }
            }
            if (
              this.documentTemplate &&
              Array.isArray(this.documentTemplate)
            ) {
              for (const docs of this.documentTemplate) {
                if (
                  docs &&
                  docs.content &&
                  Array.isArray(docs.content)
                ) {
                  for (const doc of docs.content) {
                    if (doc.elementType === 'array' && doc.structure !== '') {
                      const index = this.condoStructureTemplate.findIndex(
                        (item: any) => item.key === doc.structure);
                      if (index > -1) {
                        this.condoStructureTemplate[index].used = true;
                      }
                    }
                  }
                }
              }
            }
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
   }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle item click
   *
   * @since 1.0.0
   */
  onTitleClicked(itemId: string): void {
    this.currentItemId = itemId;
    this.formNewIsVisible = false;
    this.formNewSubElementIsVisible = false;

    const item = this.documentTemplate.filter((el: any) => { 
      return el._id.toString() === itemId.toString();
    });

    if (this.model) {
      for (const property in this.model) {
        delete this.model[property];
      }
    }

    const fieldGroup = [];
    fieldGroup.push({
      key: "itemId",
      defaultValue: item[0]._id,
      templateOptions: {
        type: "hidden",
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-12 col-xl-12",
      type: "select",
      key: "group",
      templateOptions: {
        label: this.translateService.instant("settings.document.group"),
        placeholder: this.translateService.instant("settings.document.group"),
        required: true,
        multiple: false,
        options: [
          {
            value: "amministrativo",
            label: "Gestionale amministrativo",
          },
          {
            value: "contratti",
            label: "Contratti fornitura e servizi",
          },
          {
            value: "tecnico",
            label: "Tecnica",
          },
          {
            value: "unitaImmobiliari",
            label: "Unità Immobiliari",
          },
        ],
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-5 col-xl-5",
      type: "input",
      key: "translation.it",
      defaultValue: "",
      templateOptions: {
        label: this.translateService.instant("settings.document.it"),
        placeholder: this.translateService.instant("settings.document.it"),
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-5 col-xl-5",
      type: "input",
      key: "translation.en",
      defaultValue: "",
      templateOptions: {
        label: this.translateService.instant("settings.document.en"),
        placeholder: this.translateService.instant("settings.document.en"),
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-2 col-xl-2",
      type: "select",
      key: "deleted",
      templateOptions: {
        label: this.translateService.instant("settings.document.deleted"),
        placeholder: this.translateService.instant("settings.document.deleted"),
        required: true,
        multiple: false,
        options: [
          {
            value: false,
            label: "No",
          },
          {
            value: true,
            label: "Sì",
          },
        ],
      },
    });

    this.currentItemTitle = item[0].translation.it;

    this.model = {
      group: item[0].group,
      deleted: item[0].deleted,
      translation: {
        it: item[0].translation.it,
        en: item[0].translation.en,
      }
    };

    if (item[0].content && Array.isArray(item[0].content)) {
      this.subDocumentTemplateId = item[0]._id;
      this.subDocumentTemplate = item[0].content;

    //   fieldGroup.push({
    //     className: "col-12",
    //     template:
    //       "<hr /><div><strong>" +
    //       this.translateService.instant("settings.document.secondLevel") +
    //       "</strong></div><br />",
    //   });
    //   secondLevel.forEach((el: any, index: number) => {
    //     fieldGroup.push({
    //       key: `content[${index}].id`,
    //       defaultValue: el._id,
    //       templateOptions: {
    //         type: "hidden",
    //       },
    //     });
    //     fieldGroup.push({
    //       className: "col-12 col-md-5 col-xl-5",
    //       type: "input",
    //       key: `content[${index}].it`,
    //       defaultValue: el.translation.it,
    //       templateOptions: {
    //         label: this.translateService.instant("settings.document.it"),
    //         placeholder: this.translateService.instant("settings.document.it"),
    //         required: true,
    //         minLength: 2,
    //         maxLength: 100,
    //       },
    //     });
    //     fieldGroup.push({
    //       className: "col-12 col-md-3 col-xl-3",
    //       type: "input",
    //       key: `content[${index}].en`,
    //       defaultValue: el.translation.en,
    //       templateOptions: {
    //         label: this.translateService.instant("settings.document.en"),
    //         placeholder: this.translateService.instant("settings.document.en"),
    //         required: true,
    //         minLength: 2,
    //         maxLength: 100,
    //       },
    //     });
    //     fieldGroup.push({
    //       className: "col-12 col-md-2 col-xl-2",
    //       type: "select",
    //       key: `content[${index}].deleted`,
    //       defaultValue: el.deleted,
    //       templateOptions: {
    //         label: this.translateService.instant("settings.document.deleted"),
    //         placeholder: this.translateService.instant("settings.document.deleted"),
    //         required: true,
    //         multiple: false,
    //         options: [
    //           {
    //             value: false,
    //             label: "No",
    //           },
    //           {
    //             value: true,
    //             label: "Sì",
    //           },
    //         ],
    //       },
    //     });
    //     fieldGroup.push({
    //       className: "col-12 col-md-2 col-xl-2",
    //       type: "button",
    //       key: "openSubDoc",
    //       templateOptions: {
    //         color: "accent",
    //         // tooltip: "tooltip",
    //         icon: {
    //           fontSet: "fas",
    //           fontIcon: "fa-edit",
    //         },
    //         text: this.translateService.instant("common.cta.open"),
    //         onClick: ($event) => this.onSubTitleClicked(item._id, el),
    //       },
    //     });

    //     // this.model[`sl-${el._id}-it`] = el.it;
    //     // this.model[`sl-${el._id}-en`] = el.en;
    //   });
    }

    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: fieldGroup,
      },
    ];

    this.formFirstLevelIsVisible = true;
  }

  /**
   * Handle item click
   *
   * @since 1.0.0
   */
  onSubTitleClicked(item: any): void {
    // Angular CDK 8's drag&drop computes pointer position relative to the window scroll
    // captured when the drag starts; if the page behind the dialog is scrolled down,
    // that offset desyncs the calculation and breaks reordering inside the dialog.
    window.scrollTo(0, 0);
    const dialogRef = this.dialog.open(DocumentSettingDetailDialogComponent, {
      data: {
        documentTemplateId: this.documentTemplateId,
        areaId: this.subDocumentTemplateId,
        subAreaId: item._id,
        item,
        condoStructureTemplate: this.condoStructureTemplate,
        // Full first-level areas tree, used to build the cross-content move target list
        areas: this.documentTemplate,
      },
      width: "90%",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.onGetDocument();
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle submit form
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.form);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Remove blank values from model
        Object.keys(model).forEach((key: any) => {
          if (!model[key] || model[key].valueOf() === "") {
            delete model[key];
          }
        });
        const index = this.documentTemplate.findIndex((el: any) => {
          return el._id === this.form.value.itemId;
        });
        if (index > -1) {
          this.documentTemplate[index].group= this.form.value.group;
          this.documentTemplate[index].translation= this.form.value.translation;
          this.documentTemplate[index].deleted= this.form.value.deleted;
        }

        this.cardIsLoading = true;
        const dataToSend = {
          documents: this.documentTemplate.map((el: any) => {
            return {
              _id: el._id,
              group: el.group,
              translation: el.translation,
              deleted: el.deleted,  
            };
          }),
        };

        this.subscriptions.push(
          this.settingsService
            .updateDocument(
              this.documentTemplateId,
              undefined,
              undefined,
              dataToSend
            )
            .pipe(finalize(() => (this.cardIsLoading = false)))
            .subscribe(
              (res) => {
                if (res) {
                  this.onGetDocument();

                  this.toastrService.success(res.message);
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }

  /**
   * Handle drag&drop
   *
   * @since 1.0.0
   */
  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data,
                        event.container.data,
                        event.previousIndex,
                        event.currentIndex);
    }
  }

  /**
   * Handle save document template first level
   *
   * @since 1.0.0
   */
  onSaveDocumentTemplate(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.cardIsLoading = true;
        const dataToSend = {
          documents: this.documentTemplate.map((el: any) => {
            return {
              _id: el._id,
              group: el.group,
              translation: el.translation,
              deleted: el.deleted,  
            };
          }),
        };
        this.subscriptions.push(
          this.settingsService
            .updateDocument(
              this.documentTemplateId,
              undefined,
              undefined,
              dataToSend
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                this.onGetDocument();
                this.toastrService.success(res.message);
                this.cardIsLoading = false;
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.cardIsLoading = false;
              }
            )
        );
      });
  }

  /**
   * Handle save document template second level
   *
   * @since 1.0.0
   */
  onSaveSubDocumentTemplate(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.cardIsLoading = true;
        const dataToSend = {
          documents: this.subDocumentTemplate.map((el: any) => {
            return {
              _id: el._id,
              translation: el.translation,
              deleted: el.deleted,  
            };
          }),
        };
        this.subscriptions.push(
          this.settingsService
            .updateDocument(
              this.documentTemplateId,
              this.subDocumentTemplateId,
              undefined,
              dataToSend
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                this.onGetDocument();
                this.toastrService.success(res.message);
                this.cardIsLoading = false;
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.cardIsLoading = false;
              }
            )
        );
      });
  }

  /**
   * Handle move of the selected second-level content (section) to another area.
   * Source area is the currently opened area; destination is picked from the form.
   *
   * @since 3.0.0
   */
  onMoveContentSubmit(): void {
    if (this.moveContentForm.invalid) {
      Object.keys(this.moveContentForm.controls).forEach((key) => {
        this.moveContentForm.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.move.confirmMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        const sourceAreaId = this.subDocumentTemplateId;
        const contentId = this.moveContentForm.value.contentId;
        const destAreaId = this.moveContentForm.value.destAreaId;

        this.cardIsLoading = true;
        this.subscriptions.push(
          this.settingsService
            .moveDocumentContent(
              this.documentTemplateId,
              sourceAreaId,
              destAreaId,
              contentId
            )
            .pipe(finalize(() => (this.cardIsLoading = false)))
            .subscribe(
              (result) => {
                if (result && result.status) {
                  this.toastrService.success(result.message);
                  this.moveContentForm.reset();
                  // Reload the template; the moved content disappears from this area
                  this.onGetDocument();
                }
              },
              (err) => {
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Handle add new document area
   *
   * @since 1.0.0
   */
   onAddDocumentTemplate(): void {
    this.currentItemId = "";
    this.formNewIsVisible = false;
    this.formFirstLevelIsVisible = false;
    this.formNewSubElementIsVisible = false;

    if (this.model) {
      for (const property in this.model) {
        delete this.model[property];
      }
    }

    const fieldGroup = [];
    fieldGroup.push({
      className: "col-12 col-md-12 col-xl-12",
      type: "select",
      key: "group",
      templateOptions: {
        label: this.translateService.instant("settings.document.group"),
        placeholder: this.translateService.instant("settings.document.group"),
        required: true,
        multiple: false,
        default: "amministrativo",
        options: [
          {
            value: "amministrativo",
            label: "Gestionale amministrativo",
          },
          {
            value: "contratti",
            label: "Contratti fornitura e servizi",
          },
          {
            value: "tecnico",
            label: "Tecnica",
          },
          {
            value: "unitaImmobiliari",
            label: "Unità Immobiliari",
          },
        ],
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-5 col-xl-5",
      type: "input",
      key: "translation.it",
      defaultValue: "",
      templateOptions: {
        label: this.translateService.instant("settings.document.it"),
        placeholder: this.translateService.instant("settings.document.it"),
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-5 col-xl-5",
      type: "input",
      key: "translation.en",
      defaultValue: "",
      templateOptions: {
        label: this.translateService.instant("settings.document.en"),
        placeholder: this.translateService.instant("settings.document.en"),
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-2 col-xl-2",
      type: "select",
      key: "deleted",
      templateOptions: {
        label: this.translateService.instant("settings.document.deleted"),
        placeholder: this.translateService.instant("settings.document.deleted"),
        required: true,
        multiple: false,
        default: false,
        options: [
          {
            value: false,
            label: "No",
          },
          {
            value: true,
            label: "Sì",
          },
        ],
      },
    });

    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: fieldGroup,
      },
    ];


    this.formNewIsVisible = true;
  }

  /**
   * Handle submit form new area
   *
   * @since 1.0.0
   */
   onNewFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.form);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Remove blank values from model
        Object.keys(model).forEach((key: any) => {
          if (!model[key] || model[key].valueOf() === "") {
            delete model[key];
          }
        });

        this.cardIsLoading = true;
        const dataToSend = {
          documents: {
            group: this.form.value.group,
            translation: this.form.value.translation,
            deleted: this.form.value.deleted,  
          }
         };

        this.subscriptions.push(
          this.settingsService
            .createDocument(
              this.documentTemplateId,
              undefined,
              undefined,
              dataToSend
            )
            .pipe(finalize(() => (this.cardIsLoading = false)))
            .subscribe(
              (res) => {
                if (res) {
                  this.formNewIsVisible = false;
                  this.formFirstLevelIsVisible = false;
                  this.formNewSubElementIsVisible = false;
                  this.onGetDocument();

                  this.toastrService.success(res.message);
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }

  /**
   * Handle add new element of document area
   *
   * @since 1.0.0
   */
  onAddSubDocumentTemplate(): void {
    this.formNewSubElementIsVisible = true;

    if (this.modelSubElement) {
      for (const property in this.modelSubElement) {
        delete this.modelSubElement[property];
      }
    }

    const fieldGroup = [];
    fieldGroup.push({
      key: "elementType",
      defaultValue: "object",
      templateOptions: {
        type: "hidden",
      },
    });
    fieldGroup.push({
      key: "structure",
      defaultValue: "",
      templateOptions: {
        type: "hidden",
      },
    });
    // fieldGroup.push({
    //   className: "col-12 col-md-12 col-xl-12",
    //   type: "select",
    //   key: "elementType",
    //   templateOptions: {
    //     label: this.translateService.instant("settings.document.group"),
    //     placeholder: this.translateService.instant("settings.document.group"),
    //     required: true,
    //     multiple: false,
    //     default: "object",
    //     options: [
    //       {
    //         value: "object",
    //         label: "Facoltativo",
    //       },
    //       {
    //         value: "array",
    //         label: "Dipendente dalla mappatura",
    //       },
    //     ],
    //   },
    // });
    fieldGroup.push({
      className: "col-12 col-md-5 col-xl-5",
      type: "input",
      key: "translation.it",
      defaultValue: "",
      templateOptions: {
        label: this.translateService.instant("settings.document.it"),
        placeholder: this.translateService.instant("settings.document.it"),
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-5 col-xl-5",
      type: "input",
      key: "translation.en",
      defaultValue: "",
      templateOptions: {
        label: this.translateService.instant("settings.document.en"),
        placeholder: this.translateService.instant("settings.document.en"),
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    });
    fieldGroup.push({
      className: "col-12 col-md-2 col-xl-2",
      type: "select",
      key: "deleted",
      templateOptions: {
        label: this.translateService.instant("settings.document.deleted"),
        placeholder: this.translateService.instant("settings.document.deleted"),
        required: true,
        multiple: false,
        default: false,
        options: [
          {
            value: false,
            label: "No",
          },
          {
            value: true,
            label: "Sì",
          },
        ],
      },
    });

    this.fieldsSubElement = [
      {
        fieldGroupClassName: "row",
        fieldGroup: fieldGroup,
      },
    ];
  }

  /**
   * Handle submit form new element of area
   *
   * @since 1.0.0
   */
  onNewSubElementFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.formSubElement.invalid) {
      Object.keys(this.formSubElement.controls).forEach((key) => {
        this.formSubElement.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.formSubElement);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Remove blank values from model
        Object.keys(model).forEach((key: any) => {
          if (!model[key] || model[key].valueOf() === "") {
            delete model[key];
          }
        });

        this.cardIsLoading = true;
        const dataToSend = {
          documents: {
            translation: this.formSubElement.value.translation,
            deleted: this.formSubElement.value.deleted,
            elementType: this.formSubElement.value.elementType,
            structure: this.formSubElement.value.structure,
          }
         };

        this.subscriptions.push(
          this.settingsService
            .createDocument(
              this.documentTemplateId,
              this.subDocumentTemplateId,
              undefined,
              dataToSend
            )
            .pipe(finalize(() => (this.cardIsLoading = false)))
            .subscribe(
              (res) => {
                if (res) {
                  this.onGetDocument();
                  this.formNewSubElementIsVisible = false;

                  this.toastrService.success(res.message);
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }
}
